import { Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';

const skipProp =
  (...props: string[]) =>
  (prop: PropertyKey) =>
    !props.includes(prop as string);

export const PlaybackSection = styled(Box)({
  marginTop: 24,
  marginBottom: 8,
});

export const PolicyDropdown = styled(Box, {
  shouldForwardProp: skipProp('$color'),
})<{ $color: string }>(({ $color }) => ({
  border: '1.5px solid',
  borderColor: $color,
  borderRadius: 8,
  overflow: 'hidden',
  background: 'var(--am-header-bg)',
  marginBottom: 8,
}));

export const PolicyDropdownHeader = styled(Box, {
  shouldForwardProp: skipProp('$bg', '$color', '$open'),
})<{ $bg: string; $color: string; $open: boolean }>(
  ({ $bg, $color, $open }) => ({
    padding: '10px 14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'background 0.12s',
    backgroundColor: $bg,
    borderBottom: $open ? `1.5px solid ${$color}` : 'none',
  }),
);

export const PolicyDropdownItem = styled(Box, {
  shouldForwardProp: skipProp('$isSelected', '$bg', '$isLast'),
})<{ $isSelected: boolean; $bg: string; $isLast: boolean }>(
  ({ $isSelected, $bg, $isLast }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    cursor: 'pointer',
    transition: 'background 0.12s',
    backgroundColor: $isSelected ? $bg : 'var(--am-header-bg)',
    borderBottom: $isLast ? 'none' : '1px solid',
    borderColor: 'var(--am-surface-2)',
  }),
);

export const PolicyBadge = styled(Box, {
  shouldForwardProp: skipProp('$color', '$bg', '$hasBorder'),
})<{ $color: string; $bg: string; $hasBorder?: boolean }>(
  ({ $color, $bg, $hasBorder }) => ({
    marginLeft: 6,
    fontSize: '10px',
    borderRadius: 4,
    padding: '1px 6px',
    fontWeight: 700,
    display: 'inline-block',
    lineHeight: 1.6,
    color: $color,
    backgroundColor: $bg,
    ...($hasBorder ? { border: `1px solid ${$color}` } : {}),
  }),
);

export const PolicyHeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const PolicyNameRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const PolicyIcon = styled('span')({
  fontSize: 16,
});

export const PolicyName = styled(Typography, {
  shouldForwardProp: skipProp('$color', '$isSelected'),
})<{ $color: string; $isSelected?: boolean }>(({ $color, $isSelected }) => ({
  fontSize: 13,
  fontWeight: 700,
  color: $isSelected === false ? 'var(--am-text-primary)' : $color,
}));

export const PolicyHeaderDesc = styled(Typography, {
  shouldForwardProp: skipProp('$color'),
})<{ $color: string }>(({ $color }) => ({
  fontSize: 12,
  color: $color,
  opacity: 0.75,
  marginTop: 2,
}));

export const PolicyItemDesc = styled(Typography, {
  shouldForwardProp: skipProp('$color', '$isSelected'),
})<{ $color: string; $isSelected: boolean }>(({ $color, $isSelected }) => ({
  fontSize: 12,
  color: $isSelected ? $color : 'var(--am-text-secondary)',
  marginTop: 2,
  opacity: $isSelected ? 0.75 : 1,
}));

export const PolicyItemContent = styled(Box)({
  flex: 1,
});

export const PolicyRadioOuter = styled(Box, {
  shouldForwardProp: skipProp('$color', '$isSelected'),
})<{ $color: string; $isSelected: boolean }>(({ $color, $isSelected }) => ({
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: '2px solid',
  borderColor: $isSelected ? $color : 'var(--am-border-mid)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

export const PolicyRadioInner = styled(Box, {
  shouldForwardProp: skipProp('$color'),
})<{ $color: string }>(({ $color }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: $color,
}));

export const PolicyExpandIcon = styled(ExpandMoreIcon, {
  shouldForwardProp: skipProp('$open'),
})<{ $open: boolean }>(({ $open }) => ({
  color: 'var(--am-text-disabled)',
  fontSize: 18,
  transform: $open ? 'rotate(180deg)' : 'none',
  transition: 'transform 0.15s',
}));
